import { useObserver } from 'mobx-react-lite'
import {
  SlugifiedAttributesFilterItemWithValues,
  SlugifiedAttributesFilterValue,
} from 'services/providers/bapi/entities/filters/normalize'
import { PreferredFilterType } from 'services/providers/bapi/entities/filters/types'
import { useFilterStore } from 'store/sidecar/ProductStreamContext'
import { useMainFilterTracking } from './filterTracking'

export interface AttributeFilterExternalProps {
  filter: SlugifiedAttributesFilterItemWithValues
  settings?: React.ReactNode[]
}

export interface AttributeFilterProps {
  name: string
  slug: string
  options: SlugifiedAttributesFilterValue[]
  selectedValues: string[]
  onReset: () => void
  toggleSelection: (id: string) => void
}

export const useAttributesFilterProps = (
  filter: SlugifiedAttributesFilterItemWithValues,
  { allowOnlyOneSelected }: { allowOnlyOneSelected?: boolean } = {
    allowOnlyOneSelected: false,
  },
): AttributeFilterProps => {
  const filterStore = useFilterStore()
  const filterSlug = filter.slug as PreferredFilterType

  const name = filter.name
  const filterValues = filter.values
  const filterValueIds = new Set<string>(filter.values.map(filter => filter.id))

  const selectedValues: string[] = useObserver(
    () =>
      filterStore.selectedFilters[filterSlug]?.filter(value =>
        filterValueIds.has(value),
      ) ?? [],
  )

  const trackFilterChanges = useMainFilterTracking()

  const onReset = () => {
    filterStore.clearFilterGroup(filterSlug, filterValueIds)
    trackFilterChanges(filterSlug)
  }

  const toggleSelection = (id: string) => {
    if (allowOnlyOneSelected) {
      filterStore.clearFilterGroup(filterSlug, filterValueIds)
    }
    filterStore.toggleSelected(filterSlug, id)
    trackFilterChanges(filterSlug)
  }

  return {
    name,
    slug: filterSlug,
    options: filterValues,
    onReset,
    selectedValues,
    toggleSelection,
  }
}
